import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Phone, Globe } from "lucide-react";
import { CONTACT } from "@/data/content";

const WhatsappIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413" />
  </svg>
);

const INCLUDES = [
  "Instagram + Facebook + TikTok, con frecuencia completa de publicación",
  "Gestión de comunidad e informe mensual con resultados",
  "1 desplazamiento mensual incluido",
  "Grabación de todo el contenido en cada visita",
  "Mentorías personalizadas incluidas durante los 3 meses",
];

const offerWa = `${CONTACT.whatsapp}?text=${encodeURIComponent(
  "¡Hola isavicente! Quiero reservar la oferta de último trimestre (plan aceleración + plan total)."
)}`;

export default function OfferPage() {
  return (
    <div className="relative min-h-screen bg-cream overflow-hidden">
      {/* Decorative circles */}
      <div className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full bg-mango" />
      <div className="pointer-events-none absolute top-24 -right-6 h-24 w-24 rounded-full bg-green/80" />
      <div className="pointer-events-none absolute -bottom-16 left-1/3 h-40 w-40 rounded-full bg-green/70" />
      <div className="pointer-events-none absolute -bottom-10 left-1/4 h-24 w-24 rounded-full bg-mango/90" />

      {/* Top bar */}
      <header className="relative z-10 max-w-[1200px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-end">
        <Link
          to="/"
          data-testid="offer-back-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-ink/70 hover:text-ink transition-colors"
        >
          <ArrowLeft size={16} /> Volver al inicio
        </Link>
      </header>

      <main className="relative z-10 max-w-[1200px] mx-auto px-5 sm:px-8 pb-20 pt-4">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-0">
          {/* LEFT */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:pr-14"
          >
            <div className="font-display text-2xl font-black tracking-tighter lowercase mb-6">
              isavicente<span className="text-green">.</span>
            </div>

            <span className="inline-block rounded-full bg-ink text-cream text-xs font-bold tracking-[0.15em] uppercase px-4 py-2">
              Solo último trimestre del año
            </span>
            <div className="mt-3">
              <span className="inline-block rounded-full bg-mango text-ink text-xs font-bold tracking-[0.15em] uppercase px-4 py-2">
                Reservas del 14 al 21 de septiembre
              </span>
            </div>

            <h1 className="mt-8 font-display font-black text-5xl sm:text-6xl tracking-tighter leading-[0.92]">
              cierra el año <span className="text-mango">a tope</span> con tus redes.
            </h1>

            <p className="mt-6 text-ink/80 text-lg md:text-xl font-semibold max-w-md leading-snug">
              todo lo que necesitas para los 3 meses más importantes del año, en un
              único pack, sin complicarte con planes sueltos.
            </p>

            {/* Price box */}
            <div className="mt-8 rounded-3xl border-2 border-mango bg-mango/10 p-7 max-w-md">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-mango mb-3">
                Oferta especial
              </p>
              <p className="font-display font-black tracking-tighter leading-none">
                <span className="text-green text-6xl">1.199€</span>
                <span className="text-ink/70 text-2xl font-bold"> / 3 meses</span>
              </p>
              <p className="mt-3 font-bold text-ink">
                Octubre · Noviembre · Diciembre
              </p>
            </div>
          </motion.div>

          {/* Divider */}
          <div className="hidden lg:block absolute left-1/2 top-4 bottom-24 border-l-2 border-dashed border-ink/20" />

          {/* RIGHT */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:pl-14"
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-mango mb-3">
              Qué incluye
            </p>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95]">
              plan <span className="text-green">aceleración</span> + plan{" "}
              <span className="text-mango">total</span>, todo en uno.
            </h2>

            <div className="mt-7 rounded-3xl border-2 border-ink/80 p-6 sm:p-8">
              <ul className="space-y-4">
                {INCLUDES.map((item, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`offer-include-${i}`}>
                    <span className="mt-1 grid place-items-center h-5 w-5 shrink-0 rounded-full bg-green text-ink">
                      <Check size={13} strokeWidth={3.5} />
                    </span>
                    <span className="text-ink/85 text-base md:text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="mt-6 font-clash italic text-lg md:text-xl text-ink font-semibold">
              todo el músculo de dos planes, en uno solo, para cerrar el año fuerte 🔥
            </p>

            {/* Reserve box */}
            <div className="mt-6 rounded-3xl bg-ink text-cream p-7">
              <h3 className="font-display font-bold text-2xl tracking-tight">Haz tu reserva</h3>
              <p className="text-mango font-bold mt-1">(solo tenemos 10 plazas)</p>
              <div className="mt-4 space-y-3">
                <a
                  href={`tel:${CONTACT.whatsapp.replace("https://wa.me/", "+")}`}
                  data-testid="offer-phone"
                  className="flex items-center gap-3 font-semibold hover:text-mango transition-colors"
                >
                  <Phone size={18} className="text-green" /> 634 747 862
                </a>
                <a
                  href="https://www.isavicentecm.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="offer-web"
                  className="flex items-center gap-3 font-semibold hover:text-mango transition-colors"
                >
                  <Globe size={18} className="text-green" /> www.isavicentecm.com
                </a>
              </div>
              <a
                href={offerWa}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="offer-whatsapp-btn"
                className="mt-6 inline-flex items-center gap-3 rounded-full bg-green text-ink font-bold text-base px-6 py-3.5 hover:brightness-95 transition-all"
              >
                <WhatsappIcon size={20} />
                Reservar por WhatsApp
              </a>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <span className="rounded-lg bg-green text-ink text-sm font-black tracking-wide uppercase px-4 py-2">
                Sígueme
              </span>
              <span className="rounded-lg bg-mango text-ink text-sm font-black tracking-wide uppercase px-4 py-2">
                Plazas limitadas
              </span>
            </div>

            <p className="mt-6 text-ink/50 text-sm max-w-md">
              Precio sin IVA. Promoción válida solo para el último trimestre del año.
            </p>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
